/**
 * Shared MOQ utilities for storefront quantity flows.
 * Exposed globally as window.__isalbiMoqUtils and as module exports for tests.
 */
(function initMoqUtils(globalScope) {
  const DEFAULT_MOQ = 1;

  const toPositiveInt = (value, fallback = DEFAULT_MOQ) => {
    const parsed = parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const getOverrideMoqFromB2bElement = (b2bEl) => {
    if (!b2bEl) return null;
    const raw = b2bEl.dataset ? b2bEl.dataset.overrideMoq : null;
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const getGlobalOverrideMoq = () => {
    if (typeof document === 'undefined') return null;
    const b2bEls = Array.from(document.querySelectorAll('.theme__b2b-check'));
    for (const el of b2bEls) {
      const override = getOverrideMoqFromB2bElement(el);
      if (override) return override;
    }
    return null;
  };

  const resolveEffectiveMoq = ({ overrideMoq = null, productMoq = null, fallback = DEFAULT_MOQ } = {}) => {
    if (Number.isFinite(overrideMoq) && overrideMoq > 0) return overrideMoq;
    if (Number.isFinite(productMoq) && productMoq > 0) return productMoq;
    return toPositiveInt(fallback, DEFAULT_MOQ);
  };

  const validateQuantityAgainstMoq = (quantity, moq, { allowZero = false } = {}) => {
    const normalizedMoq = toPositiveInt(moq, DEFAULT_MOQ);
    const parsedQty = parseInt(String(quantity ?? ''), 10);
    if (allowZero && parsedQty === 0) return true;
    if (!Number.isFinite(parsedQty) || parsedQty < normalizedMoq) return false;
    return parsedQty % normalizedMoq === 0;
  };

  const normalizeQuantityToMoq = (quantity, moq, { allowZero = false } = {}) => {
    const normalizedMoq = toPositiveInt(moq, DEFAULT_MOQ);
    const parsedQty = parseInt(String(quantity ?? ''), 10);
    const safeQty = Number.isFinite(parsedQty) ? parsedQty : normalizedMoq;
    if (allowZero && safeQty <= 0) {
      return {
        quantity: 0,
        moq: normalizedMoq,
        adjusted: safeQty !== 0
      };
    }
    if (safeQty <= normalizedMoq) {
      return {
        quantity: normalizedMoq,
        moq: normalizedMoq,
        adjusted: safeQty !== normalizedMoq
      };
    }
    const normalizedQty = Math.max(normalizedMoq, Math.ceil(safeQty / normalizedMoq) * normalizedMoq);
    return {
      quantity: normalizedQty,
      moq: normalizedMoq,
      adjusted: normalizedQty !== safeQty
    };
  };

  const normalizeLinesToMoq = (lines, getMoq) => {
    if (!Array.isArray(lines) || typeof getMoq !== 'function') return [];
    return lines.map((line) => {
      const moq = toPositiveInt(getMoq(line), DEFAULT_MOQ);
      const normalized = normalizeQuantityToMoq(line.quantity, moq);
      return {
        ...line,
        quantity: normalized.quantity,
        _moq: moq,
        _moqAdjusted: normalized.adjusted
      };
    });
  };

  const createMoqMemoizer = ({ ttlMs = 30000, maxEntries = 300 } = {}) => {
    const cache = new Map();

    const get = (key, compute) => {
      const now = Date.now();
      const hit = cache.get(key);
      if (hit && (now - hit.ts) < ttlMs) return hit.value;
      const value = compute();
      cache.set(key, { value, ts: now });
      if (cache.size > maxEntries) {
        const firstKey = cache.keys().next().value;
        if (firstKey !== undefined) cache.delete(firstKey);
      }
      return value;
    };

    const clear = () => cache.clear();
    return { get, clear };
  };

  const utils = {
    DEFAULT_MOQ,
    toPositiveInt,
    getOverrideMoqFromB2bElement,
    getGlobalOverrideMoq,
    resolveEffectiveMoq,
    validateQuantityAgainstMoq,
    normalizeQuantityToMoq,
    normalizeLinesToMoq,
    createMoqMemoizer
  };

  globalScope.__isalbiMoqUtils = utils;
})(typeof window !== 'undefined' ? window : globalThis);
